import {
	API_ENDPOINTS,
	DataTable,
	formatDate,
	FormInput,
	Icon,
	Icons,
	PageLayout,
	route,
	useLangContext,
	replaceKeyWithValue,
	StringHelpers,
	useAuthorisationContext,
	Variants,
	Permissions,
} from 'carrier-fe';
import { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { VenueType } from '../../types/venue';

function VenueList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { crud, fields, models } = useLangContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index.title || ':model List ',
					'model',
					crud?.models?.training_venue || 'Training Venue'
				)
			),
		[crud, models]
	);

	const handleOptionSelect = (action: any, data: VenueType) => {
		const { id = '' } = data;

		const actions: { [key: string]: () => void } = {
			view: () => {
				const searchFilter = searchParams.get('filter[search]');
				if (searchFilter) {
					sessionStorage.setItem('venueSearchFilter', searchFilter);
				}
				navigate(`/training/venue/${id}`);
			},
		};

		if (typeof action === 'string' && actions[action]) {
			actions[action]();
		}
	};

	const items = useMemo(() => {
		if (!hasPermissionAccess(Permissions.ADMIN_TRAINING_VENUE_STORE))
			return [];

		return [
			{
				label: StringHelpers.title(
					crud.buttons.create.default || 'Create'
				),
				disabled: false,
				onClick: () => navigate('/training/venue/create'),
				icon: <Icon icon={Icons.ADD} />,
			},
		];
	}, []);

	const colMapping = (data: VenueType) => {
		const cols: any = [
			{
				id: 'name',
				label: StringHelpers.title(fields?.name || 'Name'),
				value: StringHelpers.title(data.name || '-'),
				sortable: true,
			},
			{
				id: 'capacity',
				label: StringHelpers.title(fields?.capacity || 'Capacity'),
				value: data.capacity || '-',
				sortable: true,
			},
			{
				id: 'enabled',
				label: StringHelpers.title(fields?.enabled || 'Enabled'),
				value: data.enabled === '1' ? 'Y' : 'N',
				sortable: true,
			},
			{
				id: 'created_at',
				label: StringHelpers.title(fields?.created || 'Created'),
				value: formatDate(data.created_at) || '-',
				sortable: true,
			},
			{
				id: 'deleted_at',
				label: StringHelpers.title(fields?.archived || 'Archived'),
				value: formatDate(data.deleted_at) || '-',
				sortable: true,
				visibleCondition: () => !!searchParams.get('filter[trashed]'),
			},
			{
				id: 'actions',
				label: StringHelpers.title(fields?.actions || 'Actions'),
				value: data.permissions.view ? (
					<FormInput
						type="select"
						label={StringHelpers.title(fields?.actions || 'Action')}
						placeholder={StringHelpers.title(
							fields?.select || 'Select'
						)}
						name="actions"
						value=""
						onChange={(value) => {
							if (!!value && typeof value === 'string') {
								handleOptionSelect(value, data);
							}
						}}
						options={[
							{
								label: StringHelpers.title(
									crud?.view || 'View'
								),
								value: 'view',
							},
						]}
						noMargin
						disabled={!data.permissions.view}
					/>
				) : null,
			},
		];

		return cols;
	};

	return (
		<PageLayout title={title || 'Training Event Venues'}>
			<DataTable<VenueType>
				routeEndpoint={route(API_ENDPOINTS.ADMIN.TRAINING.VENUE.INDEX)}
				colMapping={colMapping}
				items={items}
				incrementPaginationButtons
				canViewArchived
				variant={Variants.Info}
			/>
		</PageLayout>
	);
}

export default VenueList;
